import { clsx } from 'clsx';
import { Check } from 'lucide-react';
import {
  createContext,
  type InputHTMLAttributes,
  type LabelHTMLAttributes,
  type ReactNode,
  type Ref,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
  useContext,
} from 'react';

// Notion keeps fields tight (4px radius); focus uses an instant outline. The boundary uses the
// control-border token (>= 3:1 against the input surface, WCAG 1.4.11); card hairlines stay faint.
const CONTROL =
  'w-full rounded-xs border border-control-border bg-surface px-2.5 text-[15px] text-ink placeholder:text-placeholder transition-[border-color] duration-150 hover:border-control-border-hover focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus disabled:cursor-not-allowed disabled:opacity-60 aria-[invalid=true]:border-bad';

interface FieldContextValue {
  id: string;
  required: boolean;
  hasError: boolean;
  hasHint: boolean;
}

const FieldContext = createContext<FieldContextValue | null>(null);

type ControlA11y = {
  id?: string | undefined;
  required?: boolean | undefined;
  'aria-required'?: boolean | 'true' | 'false' | undefined;
  'aria-describedby'?: string | undefined;
  'aria-invalid'?: boolean | 'true' | 'false' | 'grammar' | 'spelling' | undefined;
};

/**
 * Fills in what the surrounding `Field` knows (required state, described-by ids, invalid state) unless the caller
 * set it explicitly. `nativeRequired` is false for checkboxes, where `required` would mean "must be checked".
 * Exported for other ui-kit controls (`FileDropZone`); not part of the package index.
 */
export function useFieldA11y<P extends ControlA11y>(props: P, nativeRequired = true): P {
  const ctx = useContext(FieldContext);
  if (!ctx || (props.id !== undefined && props.id !== ctx.id)) return props;
  const next: P = { ...props };
  if (ctx.required) {
    if (next['aria-required'] === undefined) next['aria-required'] = true;
    if (nativeRequired && next.required === undefined) next.required = true;
  }
  if (next['aria-describedby'] === undefined) {
    const ids = describedBy(ctx.id, ctx.hasError, ctx.hasHint);
    if (ids) next['aria-describedby'] = ids;
  }
  if (next['aria-invalid'] === undefined && ctx.hasError) next['aria-invalid'] = true;
  return next;
}

export function Label({ className, ...rest }: LabelHTMLAttributes<HTMLLabelElement>) {
  // biome-ignore lint/a11y/noLabelWithoutControl: callers pass htmlFor or wrap the control
  return <label className={clsx('text-[13px] font-medium text-ink-2', className)} {...rest} />;
}

export function Input({
  className,
  ref,
  ...rest
}: InputHTMLAttributes<HTMLInputElement> & { ref?: Ref<HTMLInputElement> }) {
  const props = useFieldA11y(rest, rest.type !== 'checkbox' && rest.type !== 'radio');
  return <input ref={ref} className={clsx(CONTROL, 'h-[36px]', className)} {...props} />;
}

export function Textarea({
  className,
  ref,
  ...rest
}: TextareaHTMLAttributes<HTMLTextAreaElement> & { ref?: Ref<HTMLTextAreaElement> }) {
  const props = useFieldA11y(rest);
  return (
    <textarea ref={ref} className={clsx(CONTROL, 'min-h-20 py-2 leading-[1.55]', className)} {...props} />
  );
}

export function Select({
  className,
  ref,
  ...rest
}: SelectHTMLAttributes<HTMLSelectElement> & { ref?: Ref<HTMLSelectElement> }) {
  const props = useFieldA11y(rest);
  return <select ref={ref} className={clsx(CONTROL, 'h-[36px] pr-7', className)} {...props} />;
}

/**
 * Checkbox with a 20px box and a 36px hit area. The native input stays the control (label, keyboard, form value);
 * it is transparent and covers the hit area, and the box beneath shows its state. `className` styles the wrapper.
 */
export function Checkbox({
  className,
  ref,
  ...rest
}: Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> & { ref?: Ref<HTMLInputElement> }) {
  const props = useFieldA11y(rest, false);
  return (
    <span className={clsx('relative inline-flex size-[20px] shrink-0', className)}>
      <input
        ref={ref}
        type="checkbox"
        className="peer absolute -inset-[8px] z-10 m-0 cursor-pointer opacity-0 disabled:cursor-not-allowed"
        {...props}
      />
      <span
        aria-hidden
        className="pointer-events-none inline-flex size-full items-center justify-center rounded-xs border border-control-border bg-surface text-on-accent transition-colors duration-150 peer-hover:border-control-border-hover peer-checked:border-accent peer-checked:bg-accent peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-focus peer-disabled:opacity-50 peer-checked:[&>svg]:opacity-100"
      >
        <Check className="size-[14px] opacity-0" strokeWidth={3} />
      </span>
    </span>
  );
}

export interface FieldProps {
  /** Id of the control; label, hint and error are linked to it. */
  id: string;
  label: ReactNode;
  hint?: ReactNode;
  error?: string | undefined;
  required?: boolean;
  children: ReactNode;
  className?: string;
}

/**
 * Label + control + hint + error. The ui-kit controls inside (`Input`, `Textarea`, `Select`, `Checkbox`) with the
 * same `id` pick up `required`/`aria-required`, `aria-describedby` (only ids that are rendered) and
 * `aria-invalid` from the Field. Callers may still pass them explicitly, e.g. `describedBy(id, !!error, !!hint)`.
 */
export function Field({ id, label, hint, error, required, children, className }: FieldProps) {
  const hasError = !!error;
  const hasHint = !!hint && !hasError;
  return (
    <FieldContext.Provider value={{ id, required: !!required, hasError, hasHint }}>
      <div className={clsx('flex flex-col gap-1.5', className)}>
        <Label htmlFor={id}>
          {label}
          {required && (
            <>
              <span aria-hidden className="ml-0.5 text-bad">
                *
              </span>
              <span className="sr-only"> (required)</span>
            </>
          )}
        </Label>
        {children}
        {hasHint && (
          <p id={`${id}-hint`} className="text-[13px] leading-[1.45] text-ink-3">
            {hint}
          </p>
        )}
        {hasError && (
          <p id={`${id}-error`} role="alert" className="text-[13px] font-medium text-bad">
            {error}
          </p>
        )}
      </div>
    </FieldContext.Provider>
  );
}

/**
 * `aria-describedby` value for a Field's control: the error id while an error shows, else the hint id when the
 * Field renders a hint, else `undefined` (never an id that is not in the document).
 */
export function describedBy(id: string, hasError: boolean, hasHint = false): string | undefined {
  if (hasError) return `${id}-error`;
  return hasHint ? `${id}-hint` : undefined;
}
